import React, { useState, useEffect } from 'react';
import { ChevronDown, HelpCircle, X } from 'lucide-react';
import { logger } from '../../lib/logger';

/**
 * Welcome guide — a dismissible first-run card that explains the dashboard in plain Turkish.
 * Hidden by default once dismissed (localStorage).
 */
const STORAGE_KEY = 'cds_welcome_dismissed_v1';

const steps = [
    {
        n: '1',
        title: 'Yüksek İhtimal Şeridi',
        desc: 'En iyi 3-5 fırsat burada. "AL" veya "SAT" tavsiyesi + ne kadar kazanırsın yüzdesi gösterilir.',
    },
    {
        n: '2',
        title: 'Favorilerim',
        desc: 'Takip ettiğin coinler burada canlı. 10 saniyede bir güncellenir, hedef veya stop\'a değince uyarı alırsın.',
    },
    {
        n: '3',
        title: 'Pozisyon Hesapla',
        desc: 'Elinde ne kadar USD varsa yaz → sistem sana giriş fiyatı, hedef, stop ve kaldıraç tavsiyesi verir.',
    },
    {
        n: '4',
        title: 'Başarı Oranı',
        desc: 'Sistem son 30 günde kaç tavsiye verdi, kaçı tuttu? Buradan sisteme ne kadar güvenebileceğini görürsün.',
    },
];

export default function WelcomeGuide() {
    const [dismissed, setDismissed] = useState(false);
    const [open, setOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 768);

    useEffect(() => {
        try {
            setDismissed(localStorage.getItem(STORAGE_KEY) === '1');
        } catch (e) {
            logger.warn('welcome guide state read failed', e);
        }
    }, []);

    const close = () => {
        setDismissed(true);
        try {
            localStorage.setItem(STORAGE_KEY, '1');
        } catch (e) {
            logger.warn('welcome guide state save failed', e);
        }
    };

    if (dismissed) {
        return (
            <button
                onClick={() => {
                    setDismissed(false);
                    setOpen(true);
                    try {
                        localStorage.removeItem(STORAGE_KEY);
                    } catch (e) {
                        logger.warn('welcome guide state reset failed', e);
                    }
                }}
                data-testid="welcome-guide-reopen"
                className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-emerald-300 transition-colors"
            >
                <HelpCircle className="h-3.5 w-3.5" />
                Nasıl kullanılır?
            </button>
        );
    }

    return (
        <div
            data-testid="welcome-guide"
            className="border border-emerald-500/25 bg-gradient-to-r from-emerald-950/25 via-slate-800 to-slate-800 rounded-xl overflow-hidden shadow-lg shadow-emerald-500/5"
        >
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-700/40 transition-colors"
            >
                <div className="flex items-center gap-2.5 text-left">
                    <div className="h-7 w-7 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <HelpCircle className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-100">Hoş geldin! Nasıl kullanılır?</div>
                        <div className="text-[11px] text-slate-400">4 kolay adımda ekranı tanı</div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            close();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.stopPropagation();
                                e.preventDefault();
                                close();
                            }
                        }}
                        data-testid="welcome-guide-dismiss"
                        className="h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors cursor-pointer"
                        title="Bir daha gösterme"
                    >
                        <X className="h-4 w-4" />
                    </span>
                </div>
            </button>

            {open && (
                <div className="px-4 pb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {steps.map((s) => (
                            <div
                                key={s.n}
                                className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"
                            >
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="h-5 w-5 rounded-full bg-emerald-500 text-white text-[11px] font-bold flex items-center justify-center">
                                        {s.n}
                                    </span>
                                    <span className="text-xs font-semibold text-slate-100">{s.title}</span>
                                </div>
                                <p className="text-[11px] text-slate-300 leading-snug">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-3 text-[10px] text-slate-400 leading-relaxed">
                        <span className="md:hidden">
                            💡 Mobildeysen: alttaki sekmeleri kullan — <b>Sinyaller</b> (LONG/SHORT + kaldıraç önerileri), <b>Takip</b>, <b>Coin</b>, <b>Favoriler</b>, <b>Diğer</b>. Bir coine dokununca analizi Coin sekmesinde açılır, kaydırmaya gerek yok.
                            &nbsp;&nbsp;
                        </span>
                        ⚠ Bu bir yatırım tavsiyesi değildir — kendi riskini kendin yönet.
                    </p>
                </div>
            )}
        </div>
    );
}
